import { reviewCountsFromItems, type ReviewCounts } from "@repo/shared/reviewCounts";
import { ChartPieIcon } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { cn } from "~/lib/utils";
import { ChartTooltipContent } from "../ui/chart";
import { DONUT_SEGMENT_PROPS, DONUT_TOOLTIP_PROPS, GovernancePieSector } from "./GovernancePieSector";
import { Bone } from "./LoadingSkeletons";

interface Issue {
  readonly status: string;
}

/** Distinct work queues: accepted suggestions still need work; closed ones do not. */
export function issueWorkload(findings: ReadonlyArray<Issue>, recommendations: ReadonlyArray<Issue>, counts = reviewCountsFromItems(findings, recommendations)) {
  return [
    { key: "suggestions-open", label: "Suggestions to review", count: counts.suggestions.open, color: "var(--color-primary)" },
    { key: "suggestions-accepted", label: "Suggestions in progress", count: counts.suggestions.accepted, color: "color-mix(in oklab, var(--color-primary) 70%, var(--color-card))" },
    { key: "findings-open", label: "Findings to review", count: counts.findings.open, color: "color-mix(in oklab, var(--color-primary) 45%, var(--color-card))" },
  ];
}

export function IssuesChart({ findings, recommendations, counts, isLoading = false, unavailable = false, className }: {
  readonly counts?: ReviewCounts | undefined;
  readonly findings: ReadonlyArray<Issue>;
  readonly recommendations: ReadonlyArray<Issue>;
  readonly isLoading?: boolean;
  readonly unavailable?: boolean;
  readonly className?: string;
}) {
  const workload = issueWorkload(findings, recommendations, counts);
  const slices = workload.filter((slice) => slice.count > 0);
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  return (
    <section aria-label="Issues" aria-busy={isLoading} className={cn("@container flex min-w-0 flex-col rounded-2xl border border-border/60 bg-card p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]", className)}>
      <header className="flex items-center gap-2">
        <ChartPieIcon aria-hidden className="size-4 text-muted-foreground" />
        <h2 className="text-[15px] font-semibold text-foreground">Issues</h2>
      </header>
      <p className="mt-1 text-xs text-muted-foreground">Suggestions and findings needing action</p>
      {unavailable ? (
        <p role="status" className="my-auto py-10 text-center text-sm text-muted-foreground">Issues are temporarily unavailable.</p>
      ) : isLoading ? (
        <div role="status" className="flex flex-1 flex-col items-center gap-5 py-6">
          <span className="sr-only">Loading issues</span>
          <Bone className="size-36 rounded-full" />
          <Bone className="h-4 w-3/4" />
        </div>
      ) : total === 0 ? (
        <div className="my-auto py-10 text-center">
          <p className="text-sm font-medium text-foreground">No open issues</p>
          <p className="mt-1 text-xs text-muted-foreground">New suggestions and findings will appear here.</p>
        </div>
      ) : (
        <div data-slot="issues-chart-content" className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 pt-5 @min-[19rem]:flex-row">
          <div data-slot="issues-chart-donut" className="relative size-24 shrink-0 @min-[19rem]:size-32" role="img" aria-label={`${total} open issues`}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="count" nameKey="label" innerRadius="72%" outerRadius="100%" cornerRadius={4} {...DONUT_SEGMENT_PROPS} shape={GovernancePieSector}>
                  {slices.map((slice) => <Cell key={slice.key} fill={slice.color} />)}
                </Pie>
                <Tooltip {...DONUT_TOOLTIP_PROPS} content={<ChartTooltipContent hideLabel />} />
              </PieChart>
            </ResponsiveContainer>
            <span aria-hidden className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[28px] font-semibold leading-8 tabular-nums tracking-tight text-foreground">{total}</span>
              <span className="text-[11px] text-muted-foreground">open issues</span>
            </span>
          </div>
          <ul data-slot="issues-chart-legend" className="w-full min-w-0 space-y-2 @min-[19rem]:flex-1">
            {workload.map((slice) => (
              <li key={slice.key} className="flex items-center gap-2 text-xs">
                <span aria-hidden className="h-3 w-[3px] shrink-0 rounded-full" style={{ background: slice.color }} />
                <span className="min-w-0 flex-1 text-muted-foreground">{slice.label}</span>
                <span className="font-medium tabular-nums text-foreground">{slice.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
